import React, { useContext, useEffect, useState } from 'react';
import { Timestamp, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './IntakeForm.css';
import { LanguageContext } from '../App';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { recordConsent } from '../utils/consent';
import { translations } from '../utils/translations';
import { markGuestOnboardingCompleted, setPostAuthRedirect } from '../utils/guestIntake';
import { sendLeadToZapier } from '../utils/zapier';

function buildConsentTimestamp(acceptedAt) {
  const acceptedDate = new Date(acceptedAt);

  if (Number.isNaN(acceptedDate.getTime())) {
    return serverTimestamp();
  }

  return Timestamp.fromDate(acceptedDate);
}

function ProfileSetup() {
  const navigate = useNavigate();
  const { language } = useContext(LanguageContext);
  const t = translations[language] || translations.en;
  const location = useLocation();
  const { currentUser, setIntakeComplete } = useAuth();
  const params = new URLSearchParams(location.search);
  const isGuest = params.get('guest') === 'true';
  const redirect = params.get('redirect');
  const targetParam = params.get('target');
  const target = targetParam && targetParam.startsWith('/') ? targetParam : '/dashboard';
  const shouldContactAttorney = redirect === 'contact';
  const preferredLanguageOptions = [
    { value: 'English', label: t.header.languageOptions.en },
    { value: 'Spanish', label: t.header.languageOptions.es },
    { value: 'Haitian Creole', label: t.header.languageOptions.ht },
    { value: 'Portuguese', label: t.header.languageOptions.pt },
    { value: 'Chinese', label: t.header.languageOptions.zh },
    { value: 'Other', label: t.intake.other },
  ];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    language: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const fromContact = currentParams.get('fromContact');

    if (fromContact === 'true') {
      navigate('/dashboard', { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      name: currentUser?.displayName || prev.name,
      email: currentUser?.email || prev.email,
    }));
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => {
      if (!prev[name]) {
        return prev;
      }

      return {
        ...prev,
        [name]: '',
      };
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = t.intake.nameRequired;
    }

    if (!formData.email.trim()) {
      nextErrors.email = t.intake.emailRequired;
    }

    if (!formData.phone.trim()) {
      nextErrors.phone = t.intake.phoneRequired;
    }

    if (!formData.language.trim()) {
      nextErrors.language = t.intake.preferredLanguageRequired;
    }

    if (!formData.message.trim()) {
      nextErrors.message = t.intake.descriptionRequired;
    }

    return nextErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting || (!currentUser && !isGuest)) {
      return;
    }

    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitError('');
      return;
    }

    setErrors({});

    if (!consent) {
      alert(t.intake.mustAcceptTerms);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        language: formData.language,
        scenario: redirect || '',
        message: formData.message,
      };
      const consentRecord = await recordConsent({
        name: payload.name,
        email: currentUser?.email || payload.email,
        phone: payload.phone,
        preferredLanguage: payload.language,
        situationDescription: payload.message,
        scenario: payload.scenario,
        consentText: t.intake.contactConsentText,
        consentLanguage: language,
        isGuest,
        userId: currentUser?.uid || '',
      });
      const consentMetadata = {
        consentToContact: true,
        consentAcceptedAt: buildConsentTimestamp(consentRecord.acceptedAt),
        consentRecordId: consentRecord.recordId,
        consentLanguage: consentRecord.consentLanguage,
        termsVersion: consentRecord.termsVersion,
        privacyVersion: consentRecord.privacyVersion,
      };

      if (currentUser && db) {
        await setDoc(
          doc(db, 'users', currentUser.uid),
          {
            userId: currentUser.uid,
            name: payload.name,
            email: currentUser.email || payload.email,
            createdAt: serverTimestamp(),
            phone: payload.phone,
            preferredLanguage: payload.language,
            description: payload.message,
            situationDescription: payload.message,
            ...consentMetadata,
            profileComplete: true,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        await setDoc(
          doc(db, 'intake', currentUser.uid),
          {
            name: payload.name,
            email: currentUser.email || payload.email,
            phone: payload.phone,
            preferredLanguage: payload.language,
            situationDescription: payload.message,
            intakeComplete: true,
            ...consentMetadata,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      }

      if (currentUser) {
        setIntakeComplete(true);
      } else if (isGuest) {
        localStorage.setItem(
          'guest_intake',
          JSON.stringify({
            name: payload.name,
            email: payload.email,
            phone: payload.phone,
            preferredLanguage: payload.language,
            situationDescription: payload.message,
            scenario: payload.scenario,
            createdAt: new Date().toISOString(),
            consentToContact: true,
            consentAcceptedAt: consentRecord.acceptedAt,
            consentRecordId: consentRecord.recordId,
            consentLanguage: consentRecord.consentLanguage,
            termsVersion: consentRecord.termsVersion,
            privacyVersion: consentRecord.privacyVersion,
          })
        );
      }

      const leadData = {
        name: payload.name || currentUser?.displayName || '',
        email: currentUser?.email || payload.email || '',
        phone: payload.phone || '',
        language: payload.language || '',
        scenario: payload.scenario || '',
        notes: payload.message || '',
        message: payload.message || '',
        createdAt: new Date().toISOString(),
      };

      await sendLeadToZapier(leadData);

      if (shouldContactAttorney) {
        navigate(`/contact-flow${isGuest ? '?guest=true' : ''}`, {
          state: {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            message: formData.message,
            language: formData.language,
            scenario: payload.scenario || '',
            skipZapier: true,
          },
        });
      } else if (isGuest) {
        markGuestOnboardingCompleted();
        setPostAuthRedirect(target);
        navigate('/register', { replace: true });
      } else {
        navigate(target, { replace: true });
      }
    } catch (error) {
      setSubmitError(t.intake.errors.submitError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-content">
        <div className="form-section">
          <button className="back-button" onClick={() => navigate('/')}>
            {t.intake.back}
          </button>

          <h2>{t.intake.title}</h2>
          <p className="form-intro">{t.intake.intro}</p>

          <form onSubmit={handleSubmit} className="intake-form">
            <div className="form-group">
              <label htmlFor="name">
                {t.name} <span className="required">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder={t.name_placeholder || 'Enter your full name'}
                required
                className={errors.name ? 'input-error' : ''}
              />
              {errors.name && <p className="error-message">{errors.name}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                {t.intake.email} <span className="required">*</span>
              </label>
              <input
                id="email"
                type="email"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t.intake.emailPlaceholder}
                required
                className={errors.email ? 'input-error' : ''}
              />
              {errors.email && <p className="error-message">{errors.email}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">
                {t.intake.phone} <span className="required">*</span>
              </label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
                className={errors.phone ? 'input-error' : ''}
              />
              {errors.phone && <p className="error-message">{errors.phone}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="preferredLanguage">
                {t.intake.preferredLanguage} <span className="required">*</span>
              </label>
              <select
                id="preferredLanguage"
                name="language"
                value={formData.language}
                onChange={handleChange}
                required
                className={errors.language ? 'input-error' : ''}
              >
                <option value="" disabled>
                  {t.intake.preferredLanguagePlaceholder}
                </option>
                {preferredLanguageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.language && <p className="error-message">{errors.language}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="description">
                {t.intake.describe} <span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="5"
                required
                className={errors.message ? 'input-error' : ''}
              />
              {errors.message && <p className="error-message">{errors.message}</p>}
            </div>

            <div className="consent-group">
              <label className="consent-label" htmlFor="consent">
                <input
                  id="consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                />
                <span>
                  {t.intake.contactConsentBeforeTerms}
                  <Link
                    to="/terms"
                    className="consent-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t.legal.termsOfUse}
                  </Link>
                  {t.intake.contactConsentBetweenLinks}
                  <Link
                    to="/privacy"
                    className="consent-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t.legal.privacyPolicy}
                  </Link>
                  {t.intake.contactConsentAfterPrivacy}
                </span>
              </label>
              <p className="consent-note">{t.intake.contactConsentNote}</p>
            </div>

            {submitError && <div className="submit-message error-message-box">{submitError}</div>}

            <button type="submit" className="btn btn-primary btn-large" disabled={isSubmitting}>
              {t.intake.submit}
            </button>
            <p>{t.legal.consent}</p>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ProfileSetup;
